// Catálogo de las herramientas de iavmx que el portal lanza y administra.
// Lo comparten el Worker (roles válidos al invitar) y la SPA (tarjetas y frases).
// Datos, Facturas y Personas existen en el enum nucleo.app pero aún no tienen Worker.

export type ClaveApp = "coordinacion" | "comercial" | "datos" | "facturas" | "personas";

/** Una vista lanzable desde el portal. Vanguardia 360 y Semáforo comparten Worker (coordinacion). */
export interface Herramienta {
  clave: "v360" | "semaforo" | "comercial" | "datos" | "facturas" | "personas";
  /** Valor de nucleo.app al que pertenece el acceso. */
  app: ClaveApp;
  nombre: string;
  /** Una frase, en palabras de quien la usa. */
  frase: string;
  /** Subdominio de producción; en local se sustituye por la variable URL_*. */
  url: string;
  disponible: boolean;
  /** Para quién es, cuando aún no está lista. */
  paraQuien?: string;
}

export const HERRAMIENTAS: Herramienta[] = [
  {
    clave: "v360",
    app: "coordinacion",
    nombre: "Vanguardia 360",
    frase:
      "Pide campañas y materiales a Mercadotecnia y sigue en qué van, con fechas límite claras.",
    url: "https://360.iavmx.com",
    disponible: true,
  },
  {
    clave: "semaforo",
    app: "coordinacion",
    nombre: "Semáforo",
    frase:
      "El estado de las solicitudes entre áreas: qué va a tiempo, qué se atoró y quién tiene la pelota.",
    url: "https://semaforo.iavmx.com",
    disponible: true,
  },
  {
    clave: "comercial",
    app: "comercial",
    nombre: "Comercial",
    frase:
      "Cuentas, cotizaciones y propuestas vivas sobre Odoo. Te avisa cuando el cliente abre la propuesta.",
    url: "https://comercial.iavmx.com",
    disponible: true,
  },
  {
    clave: "datos",
    app: "datos",
    nombre: "Datos",
    frase:
      "Un tablero con las cifras de tu área y un asistente al que le preguntas en lenguaje natural.",
    url: "https://datos.iavmx.com",
    disponible: false,
    paraQuien: "En construcción",
  },
  {
    clave: "facturas",
    app: "facturas",
    nombre: "Facturas",
    frase:
      "Facturas de proveedores que llegan por correo, se validan ante el SAT y pasan a aprobación.",
    url: "https://facturas.iavmx.com",
    disponible: false,
    paraQuien: "Para Contabilidad",
  },
  {
    clave: "personas",
    app: "personas",
    nombre: "Personas",
    frase: "Contrataciones y capacitación: el alta de alguien dispara su ruta de cursos.",
    url: "https://personas.iavmx.com",
    disponible: false,
    paraQuien: "Para Recursos Humanos y Capacitación",
  },
];

/** Roles que administración puede dar por app al invitar. La clave vacía significa "sin acceso". */
export const ROLES_POR_APP: Record<ClaveApp, { clave: string; nombre: string }[]> = {
  coordinacion: [
    { clave: "solicitante", nombre: "Solicitante" },
    { clave: "produce", nombre: "Produce" },
    { clave: "coordina", nombre: "Coordina" },
    { clave: "consulta", nombre: "Consulta" },
    { clave: "direccion", nombre: "Dirección" },
  ],
  comercial: [
    { clave: "ejecutivo", nombre: "Ejecutivo" },
    { clave: "subgerente", nombre: "Subgerente" },
    { clave: "subdirector", nombre: "Subdirector" },
    { clave: "director", nombre: "Director" },
    { clave: "consulta", nombre: "Consulta" },
  ],
  datos: [{ clave: "consulta", nombre: "Consulta" }],
  facturas: [{ clave: "consulta", nombre: "Consulta" }],
  personas: [{ clave: "consulta", nombre: "Consulta" }],
};

/** Apps que se pueden otorgar hoy desde Administración (las demás llegan con su Worker). */
export const APPS_INVITABLES: ClaveApp[] = ["coordinacion", "comercial"];

export function nombreRolApp(app: ClaveApp, rol: string): string {
  const conocido = ROLES_POR_APP[app]?.find((r) => r.clave === rol)?.nombre;
  return conocido ?? (rol ? rol[0]!.toUpperCase() + rol.slice(1) : rol);
}

/** Rol con el que admin y dirección entran a cada app cuando no tienen acceso explícito. */
export function rolDeDireccion(app: ClaveApp): string {
  return app === "coordinacion" ? "direccion" : app === "comercial" ? "director" : "consulta";
}

/** Accesos por defecto según el área principal: todos entran a coordinación; Comercial y Dirección además a Comercial. */
export function accesosSugeridos(areaClave: string | null): Record<ClaveApp, string> {
  const base: Record<ClaveApp, string> = {
    coordinacion: "solicitante",
    comercial: "",
    datos: "",
    facturas: "",
    personas: "",
  };
  if (areaClave === "comercial") base.comercial = "ejecutivo";
  if (areaClave === "direccion") {
    base.coordinacion = "direccion";
    base.comercial = "director";
  }
  if (areaClave === "mercadotecnia") base.coordinacion = "coordina";
  if (areaClave === "multimedia" || areaClave === "editorial") base.coordinacion = "produce";
  return base;
}
